import {useRouter} from 'next/router';
import {useCallback, useEffect, useRef, useState} from 'react';

/**
 * from this comment
 * https://github.com/vercel/next.js/issues/2476#issuecomment-926127255
 */
const throwFakeErrorToFoolNextRouter = (): never => {
  throw 'Abort route change. Please ignore this error.';
};

export const useConfirmation = (
  shouldWarnInit: boolean,
  message: string = 'Are you sure that you want to leave?',
  onConfirm: () => void = () => {
    return;
  },
  onCancel: () => void = () => {
    return;
  },
) => {
  const router = useRouter();
  const [shouldWarn, setShouldWarn] = useState(shouldWarnInit);

  const lastHistoryState = useRef<{idx: number}>(global.history?.state);

  useEffect(() => {
    const storeLastHistoryState = (): void => {
      lastHistoryState.current = history.state;
    };
    router.events.on('routeChangeComplete', storeLastHistoryState);

    return () => {
      router.events.off('routeChangeComplete', storeLastHistoryState);
    };
  }, [router]);

  const revertTheChangeRouterJustMade = useCallback(() => {
    const state = lastHistoryState.current;
    if (state !== null && history.state !== null && state.idx !== history.state.idx) {
      history.go(lastHistoryState.current.idx < history.state.idx ? -1 : 1);
    }
  }, []);

  const killRouterEvent = useCallback(() => {
    router.events.emit('routeChangeError');

    revertTheChangeRouterJustMade();

    throwFakeErrorToFoolNextRouter();
  }, [revertTheChangeRouterJustMade, router]);

  useEffect(() => {
    let isWarned = false;

    const routeChangeStart = (url: string): void => {
      if (router.asPath !== url && shouldWarn && !isWarned) {
        isWarned = true;

        if (window.confirm(message)) {
          router.push(url);
          if (onConfirm) onConfirm();
          return;
        } else {
          if (onCancel) onCancel();
        }

        isWarned = false;
        killRouterEvent();
      }
    };

    const beforeUnload = (e: BeforeUnloadEvent): string | null => {
      if (shouldWarn && !isWarned) {
        const event = e ?? window.event;
        event.returnValue = message;
        return message;
      }
      return null;
    };

    router.events.on('routeChangeStart', routeChangeStart);
    window.addEventListener('beforeunload', beforeUnload);

    return () => {
      router.events.off('routeChangeStart', routeChangeStart);
      window.removeEventListener('beforeunload', beforeUnload);
    };
  }, [message, shouldWarn, killRouterEvent, router]);

  return {setShouldWarn};
};
